/* geo-locate.js — "where was this taken?" answered from every place the answer
 * can hide, not just the one place everybody looks.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THIS EXISTS (root fix, not symptoms)
 * ═══════════════════════════════════════════════════════════════════════════
 * Every photo-location tool in this segment — ours included, until now — asks
 * exactly one question: "does the EXIF block contain a GPS IFD?" If not, it
 * prints "no GPS" and the session is over. Reviewers name that as the defining
 * weakness of the category ("useless once metadata is stripped — most social
 * media"), and it is why the single most common real input, a photo that came
 * out of a messenger, produces a dead grey rectangle.
 *
 * But "EXIF has no GPS IFD" is not the same fact as "this file does not know
 * where it was". Measured on real files, 2026-08-02:
 *
 *   • XMP-only GPS — a JPEG whose EXIF was dropped by an editor that kept the
 *     XMP packet still spells the coordinates out in `exif:GPSLatitude`.
 *     exifr's parse() DOES surface those keys; exifr.gps() returns null, and
 *     the tool only ever called exifr.gps(). The answer was in memory and
 *     thrown away.
 *   • HEIC / AVIF — exifr returned null for every option set we tried on an
 *     AV1-branded .heic carrying a GPS IFD that our own MetaScrub reads
 *     correctly. HEIC is the iPhone default, i.e. the likeliest input of all.
 *   • Motion Photos — Samsung/Google append a whole MP4 after the JPEG's EOI
 *     marker. Stripping "the metadata" removes the EXIF GPS and leaves that
 *     video untouched, and the video carries its own `loci` location box.
 *     Measured: EXIF GPS absent, coordinates still recoverable byte-for-byte.
 *
 * So this module inverts the question. It asks every reader we already ship —
 * exifr, MetaScrub's TIFF/IFD codec, the XMP packet, VideoMeta on an embedded
 * clip — and returns the first real answer together with WHERE it came from.
 * A new hiding place is a new probe in one list, not a rewrite of two tools.
 *
 * Naming the source is not decoration. "Recovered from the hidden video inside
 * your photo" is a different statement about the user's privacy than "read
 * from EXIF", and a tool that blurs the two is lying by omission.
 *
 * Exposes `window.GeoLocate`; also CommonJS-exported so the node tests run
 * this exact file.
 */
(function (root, factory) {
    'use strict';
    var mod = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = mod;
    if (typeof window !== 'undefined') window.GeoLocate = mod;
})(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    /* ════════════════════════════════════════════════════════════════════
     * COORDINATE SANITY
     * A "found" coordinate that is out of range, or sitting exactly on
     * 0,0, is worse than no coordinate: it puts a confident pin in the Gulf
     * of Guinea. Null Island is the classic signature of a zeroed GPS block,
     * so it is refused rather than displayed.
     * ════════════════════════════════════════════════════════════════════ */
    function validCoord(lat, lon) {
        if (typeof lat !== 'number' || typeof lon !== 'number') return false;
        if (!isFinite(lat) || !isFinite(lon)) return false;
        if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return false;
        if (Math.abs(lat) < 1e-7 && Math.abs(lon) < 1e-7) return false;   // Null Island
        return true;
    }

    function coord(lat, lon, extra) {
        if (!validCoord(lat, lon)) return null;
        var g = { lat: lat, lon: lon, alt: null, direction: null, speed: null, hdop: null };
        if (extra) for (var k in extra) if (extra[k] != null) g[k] = extra[k];
        return g;
    }

    /* ════════════════════════════════════════════════════════════════════
     * XMP COORDINATES
     * XMP writes GPS as text, in three shapes that all appear in the wild:
     *   "34,55.71S"      degrees, decimal minutes, hemisphere   (Adobe)
     *   "34,55,42.6S"    degrees, minutes, seconds, hemisphere
     *   "-34.9285"       plain signed decimal                   (some phones)
     * Parsing only the first is how a tool ends up "supporting XMP" and still
     * missing half of it.
     * ════════════════════════════════════════════════════════════════════ */
    function parseXmpCoord(v) {
        if (v == null) return null;
        if (typeof v === 'number') return isFinite(v) ? v : null;
        var s = String(v).trim();
        if (!s) return null;
        var m = s.match(/^([+-]?\d+(?:\.\d+)?),(\d+(?:\.\d+)?)(?:,(\d+(?:\.\d+)?))?\s*([NSEW])?$/i);
        if (m) {
            var deg = parseFloat(m[1]), min = parseFloat(m[2]), sec = m[3] ? parseFloat(m[3]) : 0;
            var val = Math.abs(deg) + min / 60 + sec / 3600;
            if (deg < 0) val = -val;
            var ref = (m[4] || '').toUpperCase();
            if (ref === 'S' || ref === 'W') val = -Math.abs(val);
            return isFinite(val) ? val : null;
        }
        var plain = parseFloat(s);
        return isFinite(plain) ? plain : null;
    }

    // Last-resort read straight out of the XMP packet bytes. exifr merges XMP
    // into its output most of the time; when it throws on the container (it
    // does on some BMFF brands) the packet is still sitting there in plain
    // UTF-8 and a regex gets the answer the parser could not.
    var XMP_LAT = /(?:exif|Exif)?:?GPSLatitude\s*=\s*"([^"]+)"|<(?:exif|Exif):GPSLatitude>([^<]+)</;
    var XMP_LON = /(?:exif|Exif)?:?GPSLongitude\s*=\s*"([^"]+)"|<(?:exif|Exif):GPSLongitude>([^<]+)</;
    function gpsFromXmpBytes(u8) {
        // XMP packets live near the front of a file; 512 KB is far past every
        // real one and keeps a 50 MP file from being stringified whole.
        var end = Math.min(u8.length, 512 * 1024);
        var text = '';
        try { text = new TextDecoder('utf-8', { fatal: false }).decode(u8.subarray(0, end)); }
        catch (e) { return null; }
        if (text.indexOf('GPSLatitude') === -1) return null;
        var a = XMP_LAT.exec(text), b = XMP_LON.exec(text);
        if (!a || !b) return null;
        return coord(parseXmpCoord(a[1] || a[2]), parseXmpCoord(b[1] || b[2]));
    }

    /* ════════════════════════════════════════════════════════════════════
     * HIDDEN PAYLOAD AFTER END-OF-IMAGE
     * A JPEG ends at FFD9. Anything after it is invisible to every decoder
     * and to every segment-walking metadata tool, which is exactly why phone
     * makers park a whole MP4 there. Find the trailer, find its `ftyp`, and
     * the rest is an ordinary MP4 that VideoMeta already knows how to read.
     * ════════════════════════════════════════════════════════════════════ */
    function findTrailer(u8) {
        if (u8.length < 4 || u8[0] !== 0xFF || u8[1] !== 0xD8) return null;   // not a JPEG
        // Walk the segment chain to the start of scan, then look for the LAST
        // EOI: entropy-coded data can contain FFD9 by accident, so scanning
        // backwards from the end is what actually identifies the real one.
        var eoi = -1;
        for (var i = u8.length - 2; i >= 2; i--) {
            if (u8[i] === 0xFF && u8[i + 1] === 0xD9) { eoi = i + 2; break; }
        }
        if (eoi < 0 || eoi >= u8.length) return null;
        var trailerLen = u8.length - eoi;
        if (trailerLen < 32) return null;                                     // padding, not a payload
        // Locate `ftyp` inside the trailer — the MP4 may be preceded by a
        // vendor marker such as "MotionPhoto_Data".
        var kind = null, offset = -1;
        for (var p = eoi; p < u8.length - 8; p++) {
            if (u8[p] === 0x66 && u8[p + 1] === 0x74 && u8[p + 2] === 0x79 && u8[p + 3] === 0x70) {
                // `ftyp` is preceded by its own 4-byte box length.
                offset = p >= 4 ? p - 4 : p; kind = 'video'; break;
            }
        }
        return { start: eoi, length: trailerLen, kind: kind || 'data', videoOffset: offset };
    }

    /* ════════════════════════════════════════════════════════════════════
     * PROBES — each returns a coord or null, and never throws.
     * ════════════════════════════════════════════════════════════════════ */

    function fromExifr(exif) {
        if (!exif) return null;
        // exifr normalises a parsed GPS IFD to signed decimal `latitude`/
        // `longitude`; older shapes keep the raw numeric tag names.
        //
        // ⚠️ Only NUMBERS count as EXIF here. exifr merges the XMP packet into
        // the same output object, and an XMP coordinate arrives as the string
        // "34,55.71S". Accepting strings made an XMP-only photo report itself
        // as an ordinary EXIF find — the two sources tell the user different
        // things about their file, so blurring them is a wrong answer even
        // though the coordinate is right. XMP is handled by its own probe.
        var lat = typeof exif.latitude === 'number' ? exif.latitude
                : (typeof exif.GPSLatitude === 'number' ? exif.GPSLatitude : null);
        var lon = typeof exif.longitude === 'number' ? exif.longitude
                : (typeof exif.GPSLongitude === 'number' ? exif.GPSLongitude : null);
        // A raw GPSLatitude is unsigned — the hemisphere lives in the Ref tag.
        if (typeof exif.latitude !== 'number' && lat != null && /^S$/i.test(exif.GPSLatitudeRef || '')) lat = -Math.abs(lat);
        if (typeof exif.longitude !== 'number' && lon != null && /^W$/i.test(exif.GPSLongitudeRef || '')) lon = -Math.abs(lon);
        var alt = exif.GPSAltitude != null
            ? (exif.GPSAltitudeRef === 1 ? -Math.abs(exif.GPSAltitude) : exif.GPSAltitude) : null;
        return coord(lat, lon, {
            alt: alt,
            direction: exif.GPSImgDirection != null ? exif.GPSImgDirection : null,
            speed: exif.GPSSpeed != null ? exif.GPSSpeed : null,
            hdop: exif.GPSDOP != null ? exif.GPSDOP : null,
        });
    }

    function fromScrub(u8, name) {
        if (typeof window === 'undefined' || !window.MetaScrub) return { gps: null, report: null };
        var report = null;
        try { report = window.MetaScrub.inspect(u8, name); } catch (e) { return { gps: null, report: null }; }
        var g = report && report.gps;
        return { gps: g ? coord(g.lat, g.lon, { alt: g.alt }) : null, report: report };
    }

    function fromMotionVideo(blob, trailer) {
        if (typeof window === 'undefined' || !window.VideoMeta || !trailer || trailer.videoOffset < 0) {
            return Promise.resolve(null);
        }
        var clip = blob.slice(trailer.videoOffset);
        return window.VideoMeta.parse(clip).then(function (m) {
            if (!m || !m.gps) return null;
            return coord(m.gps.lat, m.gps.lon, { alt: m.gps.alt });
        }).catch(function () { return null; });
    }

    /* ════════════════════════════════════════════════════════════════════
     * CAPTURE TIME + TIMEZONE
     * OffsetTimeOriginal is the only EXIF field that survives GPS stripping
     * and still says something geographic: a UTC offset is a longitude band.
     * It is the difference between "we know nothing" and "somewhere on this
     * meridian", which is why it is extracted even when GPS is present.
     * ════════════════════════════════════════════════════════════════════ */
    /* `kind` says WHICH clock answered, because they answer different
     * questions: DateTimeOriginal is the shutter, CreateDate the moment the
     * picture became digital (the same for a phone, the scan for a scanner),
     * ModifyDate merely the last program that saved the file. Printing a
     * ModifyDate under "Taken" told every edited or re-saved photo it was shot
     * on the day it was exported — so a modify-only file gets `modified` and
     * NO `date`: the sun panel and the "taken" line must not use it. */
    function validDate(d) { return d instanceof Date && !isNaN(d.getTime()) ? d : null; }
    function captureInfo(exif) {
        var out = { date: null, raw: null, tzOffset: null, tzMinutes: null, source: null, kind: null, modified: null };
        if (!exif) return out;
        var orig = validDate(exif.DateTimeOriginal);
        var dig = validDate(exif.CreateDate) || validDate(exif.DateTimeDigitized);
        out.modified = validDate(exif.ModifyDate);
        if (orig) { out.date = orig; out.source = 'exif_original'; out.kind = 'original'; }
        else if (dig) { out.date = dig; out.source = 'exif_other'; out.kind = 'digitized'; }
        else if (out.modified) { out.kind = 'modified'; }
        else {
            var d = exif.DateTimeOriginal || exif.CreateDate || exif.DateTimeDigitized || exif.ModifyDate;
            if (d) out.raw = String(d);
        }
        var off = exif.OffsetTimeOriginal || exif.OffsetTime || exif.OffsetTimeDigitized || null;
        if (off) {
            var m = String(off).trim().match(/^([+-])(\d{2}):?(\d{2})$/);
            if (m) {
                out.tzOffset = m[1] + m[2] + ':' + m[3];
                out.tzMinutes = (m[1] === '-' ? -1 : 1) * (parseInt(m[2], 10) * 60 + parseInt(m[3], 10));
            }
        }
        return out;
    }

    /* ════════════════════════════════════════════════════════════════════
     * WHY THERE IS NO LOCATION
     * "No GPS" is four different facts that need four different next steps,
     * and the page used to print one of them — "social networks strip GPS" —
     * for all four, including a camera original straight off the phone.
     *
     * The loudest one is Android's. Since Android 10 the system wipes the
     * location out of every photo it hands an app that lacks
     * ACCESS_MEDIA_LOCATION, and the photo picker wipes it for everyone
     * (AOSP MediaProvider, RedactionUtils.getRedactionRanges). Chrome does not
     * hold that permission, so a web page on Android never receives the
     * coordinates of a gallery photo: measured on our own traffic (30 days to
     * 28.10.2026), camera originals carried GPS in 2–3 of 60–66 Android
     * sessions against 21–38 of 32–77 on Windows. The wipe is recognisable:
     * MediaProvider writes ZEROS over the value bytes of every GPS tag and
     * leaves the entries in place, so the block is still there with
     * GPSVersionID 0.0.0.0 and 0/0 rationals — values no camera writes. That
     * is the difference between "your phone removed it on the way here" (the
     * original still knows) and "the camera never recorded it" (nobody does).
     * ════════════════════════════════════════════════════════════════════ */
    var TAG_GPS_IFD = 0x8825;
    // GPSVersionID, Latitude(Ref), Longitude(Ref): the tags that carry the
    // position. A wiped block has every one of them zeroed; other GPS tags
    // (0x1F positioning error, vendor ones) are not on Android's list.
    var GPS_POSITION_TAGS = { 0: 1, 1: 1, 2: 1, 3: 1, 4: 1 };

    function allZero(u8) {
        if (!u8 || !u8.length) return true;
        for (var i = 0; i < u8.length; i++) if (u8[i] !== 0) return false;
        return true;
    }

    /**
     * gpsBlock(scrubReport, exif) → 'none' | 'wiped' | 'no_fix' | 'coords'
     *   none   — no GPS block at all
     *   wiped  — the block is there, its position values are zero bytes
     *   no_fix — a block without a position (the camera had no satellites)
     *   coords — a block with a position (valid or not is locate()'s call)
     * Reads MetaScrub's raw TIFF entries, which exist for every container it
     * walks (JPEG, HEIC/AVIF, PNG eXIf, WebP, TIFF); exifr's view is the
     * fallback for whatever MetaScrub could not open.
     */
    function gpsBlock(scrubReport, exif) {
        var parsed = scrubReport && scrubReport._exif, sub = null;
        if (parsed && parsed.ifd0 && parsed.ifd0.entries) {
            parsed.ifd0.entries.forEach(function (e) { if (e.tag === TAG_GPS_IFD && e.sub) sub = e.sub; });
        }
        if (sub) {
            var pos = (sub.entries || []).filter(function (e) { return GPS_POSITION_TAGS[e.tag]; });
            var hasLatLon = pos.some(function (e) { return e.tag === 2; }) && pos.some(function (e) { return e.tag === 4; });
            if (pos.length && pos.every(function (e) { return allZero(e.val); })) return 'wiped';
            return hasLatLon ? 'coords' : 'no_fix';
        }
        if (exif) {
            if (String(exif.GPSVersionID || '') === '0.0.0.0') return 'wiped';
            var keys = Object.keys(exif).filter(function (k) { return /^GPS/.test(k); });
            if (keys.length) return (exif.GPSLatitude != null && exif.GPSLongitude != null) ? 'coords' : 'no_fix';
        }
        return 'none';
    }

    var SCREENSHOT_NAME = /screen[ _-]?shot|screen[ _-]?capture|скриншот|снимок экрана|знімок екрана|bildschirmfoto|captura de pantalla|captura de tela|capture d.[ée]cran|tangkapan layar|스크린샷|لقطة.?شاشة/i;

    function looksLikeCamera(exif) {
        if (!exif || !(exif.Make || exif.Model)) return false;
        return exif.ExposureTime != null || exif.FNumber != null || exif.ISO != null || exif.FocalLength != null;
    }

    /**
     * whyNoLocation(file, exif, scrubReport, block) → reason code, for a file
     * locate() found no coordinate in:
     *   wiped       — the GPS block was zeroed in transit (Android)
     *   no_fix      — the camera wrote a GPS block without a position
     *   screenshot  — a screen capture: there was never a place to record
     *   camera      — a camera original without a GPS block
     *   resaved     — some metadata survived a program, the position did not
     *   stripped    — nothing at all: messenger, social network, web download
     */
    function whyNoLocation(file, exif, scrubReport, block) {
        exif = exif || {};
        if (block === 'wiped') return 'wiped';
        if (block === 'no_fix' || block === 'coords') return 'no_fix';
        if (SCREENSHOT_NAME.test((file && file.name) || '') || /screenshot/i.test(String(exif.Software || ''))) return 'screenshot';
        if (looksLikeCamera(exif)) return 'camera';
        var found = scrubReport && scrubReport.findings && scrubReport.findings.length;
        return (Object.keys(exif).length || found) ? 'resaved' : 'stripped';
    }

    /**
     * fileClock(file, capture, now) — the file system's date, read against the
     * capture date. A camera writes the photo within seconds of the shutter, so
     * when lastModified lands right after DateTimeOriginal, this IS the camera
     * original on this device and the capture instant is known to the second —
     * which is what lets the page say "taken 24 minutes ago" honestly instead
     * of guessing the timezone. Without OffsetTimeOriginal the capture clock is
     * read in the viewer's zone, which is the phone's own zone exactly in the
     * case being tested. A file whose clocks disagree is a copy (saved from a
     * chat, downloaded, re-stamped by a picker); its lastModified still bounds
     * the capture from above — nothing can be photographed after its file was
     * written — and that is all it is used for.
     */
    function fileClock(file, capture, now) {
        now = now || Date.now();
        var lm = (file && +file.lastModified) || 0;
        var out = { lastModified: lm > 0 ? new Date(lm) : null, sameClock: false, instant: null, ageS: null };
        if (!lm || !capture || !capture.date) return out;
        var d = capture.date, inst = d.getTime();
        if (capture.tzMinutes != null) {
            inst = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes(), d.getSeconds(), d.getMilliseconds())
                - capture.tzMinutes * 60000;
        }
        var lag = lm - inst;
        if (lag >= -60000 && lag <= 300000) {
            out.sameClock = true;
            out.instant = new Date(inst);
            out.ageS = Math.round((now - inst) / 1000);
        }
        return out;
    }

    /**
     * captureInstant(capture, clock, lon, exif) → { date, exact } | null — the MOMENT
     * of capture, which is what the sun maths needs. exifr builds the Date from
     * the camera's wall-clock digits in the VIEWER's zone: printed, that is the
     * right reading ("12:34 where it was taken"); as an instant it is off by the
     * difference between the two zones, so a Moscow reader of a New York photo
     * put the sun seven hours away. Exact when the file names its offset or its
     * own clock confirms the shutter (fileClock), or the GPS block carries the
     * satellite's UTC time of the fix; otherwise, with a longitude, the nearest
     * whole-hour solar zone — marked as an estimate (Kyiv in June came out an
     * hour off that way, while the same file's GPSTimeStamp was exact).
     */
    function gpsUtc(exif) {
        if (!exif || !exif.GPSDateStamp || exif.GPSTimeStamp == null) return null;
        var dm = String(exif.GPSDateStamp).match(/^(\d{4})[:\-](\d{2})[:\-](\d{2})/);
        var t = exif.GPSTimeStamp;
        var tp = Array.isArray(t) || (t && typeof t.length === 'number' && typeof t !== 'string') ? Array.prototype.slice.call(t) : String(t).split(':');
        if (!dm || tp.length < 3) return null;
        var h = +tp[0], mi = +tp[1], sec = +tp[2];
        if (![h, mi, sec].every(isFinite) || h > 24 || mi > 59 || sec >= 61) return null;
        var ms = Date.UTC(+dm[1], +dm[2] - 1, +dm[3], h, mi, 0) + Math.round(sec * 1000);
        return isFinite(ms) ? new Date(ms) : null;
    }
    function captureInstant(capture, clock, lon, exif) {
        if (!capture || !capture.date) return null;
        var d = capture.date;
        var wall = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes(), d.getSeconds(), d.getMilliseconds());
        if (capture.tzMinutes != null) return { date: new Date(wall - capture.tzMinutes * 60000), exact: true };
        // A GPS fix can be minutes older than the shutter; trusted only while it
        // sits within half a day of the wall clock, i.e. is the same moment.
        var g = gpsUtc(exif);
        if (g && Math.abs(g.getTime() - wall) <= 14 * 3600000) return { date: g, exact: true };
        if (clock && clock.sameClock && clock.instant) return { date: clock.instant, exact: true };
        if (typeof lon === 'number' && isFinite(lon)) return { date: new Date(wall - Math.round(lon / 15) * 3600000), exact: false };
        return null;
    }

    /* ════════════════════════════════════════════════════════════════════
     * PUBLIC API
     * ════════════════════════════════════════════════════════════════════ */

    var SOURCE_ORDER = ['exif', 'tiff', 'xmp', 'motion'];

    /**
     * locate(file, {exif}) → Promise<result>
     *
     * `exif` is the already-parsed exifr output when the caller has one, so
     * the file is not parsed twice. Never rejects: an unreadable file comes
     * back with gps:null and a warning, because a batch run must be able to
     * report the skip honestly rather than die on one bad photo.
     */
    function locate(file, opts) {
        opts = opts || {};
        var result = {
            gps: null, source: null, recovered: false,
            candidates: {},          // every probe's answer, for the report
            exif: opts.exif || null,
            scrub: null, hidden: null,
            capture: captureInfo(opts.exif),
            block: 'none', why: null, clock: null,
            warnings: [],
        };

        return file.arrayBuffer().then(function (ab) {
            var u8 = new Uint8Array(ab);

            // 1 — EXIF GPS IFD (authoritative when present)
            result.candidates.exif = fromExifr(result.exif);

            // 2 — the same TIFF block read by our own IFD codec. Covers the
            //     containers exifr gives up on (HEIC/AVIF, PNG eXIf, WebP,
            //     .tif) and cross-checks the ones it does not.
            var s = fromScrub(u8, file.name || '');
            result.candidates.tiff = s.gps;
            result.scrub = s.report;

            // 3 — XMP packet, from exifr's merged keys or from the raw bytes
            var xmp = null;
            if (result.exif && typeof result.exif.GPSLatitude === 'string' && typeof result.exif.GPSLongitude === 'string') {
                xmp = coord(parseXmpCoord(result.exif.GPSLatitude), parseXmpCoord(result.exif.GPSLongitude));
            }
            if (!xmp) xmp = gpsFromXmpBytes(u8);
            result.candidates.xmp = xmp;

            // 4 — hidden trailer (Motion Photo / MPF). Detected here even when
            //     it holds no GPS, because "there is a video of your room
            //     inside this photo" is a finding in its own right.
            var trailer = findTrailer(u8);
            if (trailer) result.hidden = { bytes: trailer.length, kind: trailer.kind, offset: trailer.start };

            if (result.candidates.exif || result.candidates.tiff || result.candidates.xmp) {
                return null;                       // no need to parse the clip
            }
            return fromMotionVideo(file, trailer);
        }).then(function (motionGps) {
            if (motionGps) result.candidates.motion = motionGps;

            for (var i = 0; i < SOURCE_ORDER.length; i++) {
                var k = SOURCE_ORDER[i];
                if (result.candidates[k]) { result.gps = result.candidates[k]; result.source = k; break; }
            }
            result.recovered = !!result.source && result.source !== 'exif';
            result.block = gpsBlock(result.scrub, result.exif);
            if (!result.gps) result.why = whyNoLocation(file, result.exif, result.scrub, result.block);
            result.clock = fileClock(file, result.capture, opts.now);

            // A disagreement between two readers of the same file is a real
            // signal (re-tagged photo, edited GPS block) — surface it instead
            // of silently preferring the first one.
            var e = result.candidates.exif, t = result.candidates.tiff;
            if (e && t && (Math.abs(e.lat - t.lat) > 1e-4 || Math.abs(e.lon - t.lon) > 1e-4)) {
                result.warnings.push('gps_sources_disagree');
            }
            return result;
        }).catch(function (err) {
            result.warnings.push('read_failed:' + ((err && err.message) || err));
            if (!result.why) result.why = 'unreadable';
            return result;
        });
    }

    /* ════════════════════════════════════════════════════════════════════
     * FORMATTING — shared so photo and video never drift apart
     * ════════════════════════════════════════════════════════════════════ */

    function toDms(value, isLat) {
        var ref = isLat ? (value < 0 ? 'S' : 'N') : (value < 0 ? 'W' : 'E');
        var a = Math.abs(value);
        var d = Math.floor(a);
        var mFloat = (a - d) * 60;
        var m = Math.floor(mFloat);
        var s = (mFloat - m) * 60;
        return d + '°' + String(m).padStart(2, '0') + "'" + s.toFixed(2).padStart(5, '0') + '"' + ref;
    }

    function formatCoords(lat, lon, style) {
        if (style === 'dms') return toDms(lat, true) + ' ' + toDms(lon, false);
        if (style === 'dm') {
            var f = function (v, isLat) {
                var ref = isLat ? (v < 0 ? 'S' : 'N') : (v < 0 ? 'W' : 'E');
                var a = Math.abs(v), d = Math.floor(a);
                return d + '°' + ((a - d) * 60).toFixed(4) + "'" + ref;
            };
            return f(lat, true) + ' ' + f(lon, false);
        }
        return lat.toFixed(6) + ', ' + lon.toFixed(6);
    }

    /* A Nominatim `display_name` is a comma-salad that repeats the city in two
     * languages and ends in a postcode. What a person wants first is the
     * shortest phrase that identifies the place; the full string stays
     * available underneath. Built from the structured `address` object so it
     * degrades sensibly when a component is missing. */
    var PLACE_KEYS = ['tourism', 'attraction', 'building', 'amenity', 'shop', 'leisure', 'historic'];
    var AREA_KEYS = ['neighbourhood', 'suburb', 'city_district', 'quarter'];
    var CITY_KEYS = ['city', 'town', 'village', 'municipality', 'hamlet', 'county'];
    function shortAddress(data) {
        if (!data) return { line1: '', line2: '', full: '' };
        var a = data.address || {};
        var pick = function (keys) { for (var i = 0; i < keys.length; i++) if (a[keys[i]]) return a[keys[i]]; return ''; };
        var place = pick(PLACE_KEYS);
        var street = a.road ? (a.road + (a.house_number ? ' ' + a.house_number : '')) : '';
        var city = pick(CITY_KEYS);
        var area = pick(AREA_KEYS);
        var country = a.country || '';
        var line1 = place || street || area || city || (data.display_name || '').split(',')[0] || '';
        var rest = [];
        if (line1 !== city && city) rest.push(city);
        if (a.state && a.state !== city) rest.push(a.state);
        if (country) rest.push(country);
        // de-dupe: Nominatim happily returns "Adelaide, Аделаида" in one line
        var seen = {}, line2 = [];
        rest.forEach(function (x) { var k = String(x).toLowerCase(); if (!seen[k]) { seen[k] = 1; line2.push(x); } });
        return { line1: line1, line2: line2.join(', '), full: data.display_name || '', cc: (a.country_code || '').toUpperCase() };
    }

    return {
        locate: locate,
        findTrailer: findTrailer,
        parseXmpCoord: parseXmpCoord,
        gpsFromXmpBytes: gpsFromXmpBytes,
        captureInfo: captureInfo,
        gpsBlock: gpsBlock,
        whyNoLocation: whyNoLocation,
        fileClock: fileClock,
        captureInstant: captureInstant,
        gpsUtc: gpsUtc,
        validCoord: validCoord,
        formatCoords: formatCoords,
        toDms: toDms,
        shortAddress: shortAddress,
        SOURCE_ORDER: SOURCE_ORDER,
    };
});
